import { useEffect, useMemo, useRef, useState } from "react";
import type {
  CreateTrailRequest,
  Hasher,
  Kennel,
  NewHareInput,
  Trail,
} from "@hash/shared";
import { predictTrailNumber, trailStartAt } from "@hash/shared";
import { api } from "../api/client";
import {
  loadHashers,
  reloadHashers,
  searchHashers,
  useHashers,
} from "../stores/hasherStore";
import { parseApiError } from "../api/errors";

interface Props {
  // KennelId to pre-select in the kennel dropdown — usually the most-recent
  // trail's kennel so the common case is one tap.
  defaultKennelId: string;
  // "trail" (default) is the numbered run; "special" is a one-off named event
  // (Red Dress Run, campout, …) — Milestone 9. Special events swap the number
  // field for a name, make the start location + hares optional, and add an
  // optional special-fee section since they charge everyone.
  mode?: "trail" | "special";
  onCreated: (trail: Trail) => void;
  onCancel: () => void;
}

// A hare is either an existing hasher or one we'll create alongside the trail.
type HareSelection =
  | { kind: "existing"; hasher: Hasher }
  | { kind: "new"; data: NewHareInput };

export function NewTrailPage({
  defaultKennelId,
  mode = "trail",
  onCreated,
  onCancel,
}: Props) {
  const special = mode === "special";
  const [kennels, setKennels] = useState<Kennel[]>([]);
  const [kennelId, setKennelId] = useState(defaultKennelId);
  const [number, setNumber] = useState("");
  // The number auto-populates from the date (slotting chronologically into the
  // kennel's existing trails) until the misman edits it by hand.
  const [numberTouched, setNumberTouched] = useState(false);
  const [kennelTrails, setKennelTrails] = useState<Trail[]>([]);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(todayLocal());
  // Trail start time-of-day ("HH:mm"); special events keep `start` for the
  // optional meeting-spot location instead.
  const [startTime, setStartTime] = useState("");
  const [start, setStart] = useState("");
  const [hares, setHares] = useState<HareSelection[]>([]);
  // Custom hare listing (free text). When on, the hare picker is hidden and this
  // text is shown as the hares instead — no hasher hare is required.
  const [customHareOn, setCustomHareOn] = useState(false);
  const [customHare, setCustomHare] = useState("");
  // Special-event fee (Milestone 9). Prices are entered in dollars and
  // converted to integer cents on submit. Off by default.
  const [specialFee, setSpecialFee] = useState(false);
  const [memberPrice, setMemberPrice] = useState("");
  const [nonMemberPrice, setNonMemberPrice] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Kennels are a small list, so fetch them all once for the dropdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const collected: Kennel[] = [];
      let next: string | undefined = undefined;
      do {
        const res = await api.kennels.list({ cursor: next, limit: 200 });
        if (cancelled) return;
        collected.push(...res.kennels);
        next = res.cursor;
      } while (next);
      if (cancelled) return;
      setKennels(collected);
      // If the prop kennel doesn't exist (e.g., first-ever trail with no
      // recent default), fall back to the first kennel so the form is usable.
      if (!collected.find((k) => k.id === defaultKennelId) && collected[0]) {
        setKennelId(collected[0].id);
      }
    })().catch((e) => console.error("Failed to load kennels", e));
    return () => {
      cancelled = true;
    };
  }, [defaultKennelId]);

  const kennelById = useMemo(() => {
    const m = new Map<string, Kennel>();
    for (const k of kennels) m.set(k.id, k);
    return m;
  }, [kennels]);

  const selectedKennel = kennelById.get(kennelId);

  // Numbered trails autopopulate from the date, which means we need the kennel's
  // existing trails to slot into. Fetch them whenever the kennel changes (trail
  // mode only — special events have no number). A wide range pulls them all.
  useEffect(() => {
    if (special || !kennelId) return;
    let cancelled = false;
    (async () => {
      const { trails } = await api.trails.list("1970-01-01", "2999-12-31");
      if (cancelled) return;
      setKennelTrails(trails.filter((t) => t.kennelId === kennelId));
    })().catch((e) => console.error("Failed to load kennel trails", e));
    return () => {
      cancelled = true;
    };
  }, [kennelId, special]);

  // Suggest the next trail number from the chosen date until the misman edits it
  // by hand. Stays blank when the kennel has no trails yet (they type the first).
  useEffect(() => {
    if (special || numberTouched) return;
    const predicted = predictTrailNumber(kennelTrails, date);
    setNumber(predicted === undefined ? "" : String(predicted));
  }, [special, numberTouched, kennelTrails, date]);

  // Prefill the start time from the selected kennel's schedule (default 7pm)
  // so the field shows the usual time; the misman can override it per-trail.
  useEffect(() => {
    if (special) return;
    setStartTime(selectedKennel?.schedule?.time ?? "19:00");
  }, [special, selectedKennel?.schedule?.time]);

  const numberInt = Number.parseInt(number, 10);
  const memberCents = toCents(memberPrice);
  const nonMemberCents = toCents(nonMemberPrice);
  // A special fee, when on, needs both prices and member ≤ non-member.
  const feeValid =
    !special ||
    !specialFee ||
    (memberCents !== null &&
      nonMemberCents !== null &&
      memberCents <= nonMemberCents);
  const canSubmit =
    !submitting &&
    kennelId.length > 0 &&
    date.length > 0 &&
    feeValid &&
    (special
      ? name.trim().length > 0
      : Number.isInteger(numberInt) && numberInt > 0);

  const existingHareIds = () =>
    hares
      .filter((h): h is Extract<HareSelection, { kind: "existing" }> =>
        h.kind === "existing"
      )
      .map((h) => h.hasher.id);
  const newHareInputs = () =>
    hares
      .filter((h): h is Extract<HareSelection, { kind: "new" }> =>
        h.kind === "new"
      )
      .map((h) => h.data);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    // With a custom hare listing the hare picker is suppressed, so no hasher
    // hares are sent — the free text stands in for them.
    const customHareValue = customHareOn
      ? customHare.trim() || undefined
      : undefined;
    const hareIds = customHareOn ? [] : existingHareIds();
    const newHares = customHareOn ? [] : newHareInputs();
    try {
      const event = special
        ? await api.trails.createSpecial({
            kennelId,
            name: name.trim(),
            date,
            startLocation: start.trim() || undefined,
            hareHasherIds: hareIds,
            newHares,
            customHare: customHareValue,
            specialFee: specialFee || undefined,
            memberPrice: specialFee ? memberCents ?? undefined : undefined,
            nonMemberPrice: specialFee
              ? nonMemberCents ?? undefined
              : undefined,
          })
        : await api.trails.create({
            kennelId,
            number: numberInt,
            date,
            title: title.trim() || undefined,
            startAt:
              trailStartAt(
                date,
                startTime.trim() || undefined,
                selectedKennel?.schedule?.timeZone
              ) ?? undefined,
            hareHasherIds: hareIds,
            newHares,
            customHare: customHareValue,
          } satisfies CreateTrailRequest);
      // New hares are created server-side as part of the trail; pull them into
      // the shared store so later searches find them.
      if (newHares.length > 0) void reloadHashers();
      onCreated(event);
    } catch (e) {
      // A 409 can come from a duplicate new-hare name; refresh the store so the
      // existing hasher is searchable.
      if (parseApiError(e).status === 409) void reloadHashers();
      setError(
        e instanceof Error
          ? e.message
          : `Failed to create ${special ? "event" : "trail"}`
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="px-4 pt-4 space-y-4">
        <Field label="Kennel">
          {kennels.length === 0 ? (
            <p className="text-sm text-slate-500">
              No kennels yet — an admin must create one first.
            </p>
          ) : (
            <select
              value={kennelId}
              onChange={(e) => setKennelId(e.target.value)}
              className={inputCls}
            >
              {!kennelById.has(kennelId) && (
                <option value="" disabled>
                  Select a kennel…
                </option>
              )}
              {kennels.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        {special && (
          <Field label="Event name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Red Dress Run"
              className={inputCls}
            />
          </Field>
        )}

        <Field label="Date">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={inputCls}
          />
        </Field>

        {!special && (
          <>
            <Field label="Trail number">
              <input
                type="number"
                inputMode="numeric"
                min={1}
                value={number}
                onChange={(e) => {
                  setNumberTouched(true);
                  setNumber(e.target.value);
                }}
                placeholder="e.g. 1742"
                className={inputCls}
              />
            </Field>

            <Field label="Title (optional)">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Full Moon Trail"
                className={inputCls}
              />
            </Field>

            <Field label="Start time">
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className={inputCls}
              />
            </Field>
          </>
        )}

        {special && (
          <Field label="Start (optional)">
            <input
              value={start}
              onChange={(e) => setStart(e.target.value)}
              placeholder="Cross-streets, bar name, …"
              className={inputCls}
            />
          </Field>
        )}

        {special && (
          <Field label="Fee (optional)">
            <label className="flex items-center gap-2 mb-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={specialFee}
                onChange={(e) => setSpecialFee(e.target.checked)}
                className="w-5 h-5"
              />
              Charge everyone a special fee (members included)
            </label>
            {specialFee && (
              <div className="flex gap-3">
                <label className="flex-1 block">
                  <span className="block text-xs text-slate-500 mb-1">
                    Member $
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={memberPrice}
                    onChange={(e) => setMemberPrice(e.target.value)}
                    placeholder="0"
                    className={inputCls}
                  />
                </label>
                <label className="flex-1 block">
                  <span className="block text-xs text-slate-500 mb-1">
                    Non-member $
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={nonMemberPrice}
                    onChange={(e) => setNonMemberPrice(e.target.value)}
                    placeholder="0"
                    className={inputCls}
                  />
                </label>
              </div>
            )}
            {specialFee && !feeValid && (
              <p className="mt-1 text-xs text-rose-600">
                Enter both prices; member price can't exceed non-member.
              </p>
            )}
          </Field>
        )}

        <Field label="Hares (optional)">
          <label className="flex items-center gap-2 mb-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={customHareOn}
              onChange={(e) => setCustomHareOn(e.target.checked)}
              className="w-5 h-5"
            />
            Custom hare listing (free text)
          </label>

          {customHareOn ? (
            <>
              <input
                type="text"
                value={customHare}
                onChange={(e) => setCustomHare(e.target.value)}
                placeholder="e.g. I-Feel Tower & Rafa"
                aria-label="Custom hare listing"
                className={inputCls}
              />
              <p className="mt-2 text-sm text-slate-500">
                Shown as the hares instead of a hasher — no hare needs to be
                assigned.
              </p>
            </>
          ) : (
            <>
              {hares.length === 0 && (
                <p className="mb-2 text-sm text-slate-500">
                  Leave empty to create the trail with hares TBD — assign them
                  later on the details page.
                </p>
              )}
              {hares.length > 0 && (
                <ul className="flex flex-wrap gap-2 mb-2">
                  {hares.map((h, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-slate-100 text-sm"
                    >
                      <span>
                        {h.kind === "existing" ? (
                          <>
                            {h.hasher.name}
                            <span className="ml-1 text-xs text-slate-500">
                              {h.hasher.homeKennel}
                            </span>
                          </>
                        ) : (
                          `${h.data.name} (new)`
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setHares(hares.filter((_, j) => j !== i))
                        }
                        aria-label="Remove hare"
                        className="w-7 h-7 rounded-full flex items-center justify-center text-slate-500 active:bg-slate-200"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="w-full h-12 rounded-lg border border-dashed border-slate-300 text-slate-700 active:bg-slate-50"
              >
                + Add hare
              </button>
            </>
          )}
        </Field>

        {error && (
          <p className="text-sm text-rose-600" role="alert">
            {error}
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 h-12 rounded-lg border border-slate-300 text-slate-700 font-medium active:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="flex-1 h-12 rounded-lg bg-slate-900 text-white font-medium active:bg-slate-700 disabled:bg-slate-300"
          >
            {submitting
              ? "Creating…"
              : special
                ? "Create event"
                : "Create trail"}
          </button>
        </div>
      </div>

      {pickerOpen && (
        <HarePickerModal
          kennels={kennels}
          defaultHomeKennelId={kennelId}
          onSelect={(sel) => {
            setHares((prev) => [...prev, sel]);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function HarePickerModal({
  kennels,
  defaultHomeKennelId,
  onSelect,
  onClose,
}: {
  kennels: Kennel[];
  defaultHomeKennelId: string;
  onSelect: (sel: HareSelection) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState<NewHareInput | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Hare search runs against the shared client-side store.
  const { hashers: roster } = useHashers();

  useEffect(() => {
    inputRef.current?.focus();
    // Refresh the roster if it's gone stale when the picker opens.
    loadHashers().catch(() => {});
  }, []);

  const results = useMemo(() => searchHashers(query), [query, roster]);

  if (creating) {
    const canAdd =
      creating.name.trim().length > 0 && creating.homeKennelId.length > 0;
    return (
      <Overlay onClose={onClose} title="New hasher">
        <div className="space-y-3">
          <Field label="Name">
            <input
              value={creating.name}
              onChange={(e) =>
                setCreating({ ...creating, name: e.target.value })
              }
              className={inputCls}
              autoFocus
            />
          </Field>
          <Field label="Home kennel">
            <select
              value={creating.homeKennelId}
              onChange={(e) =>
                setCreating({ ...creating, homeKennelId: e.target.value })
              }
              className={inputCls}
            >
              {kennels.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Phone (optional)">
            <input
              type="tel"
              inputMode="tel"
              value={creating.phone ?? ""}
              onChange={(e) =>
                setCreating({ ...creating, phone: e.target.value })
              }
              placeholder="+15551234567"
              className={inputCls}
            />
          </Field>
          <p className="text-xs text-slate-500">
            They won't be able to log in until an admin grants access.
          </p>
        </div>
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => setCreating(null)}
            className="flex-1 h-12 rounded-lg border border-slate-300 text-slate-700 font-medium active:bg-slate-50"
          >
            Back
          </button>
          <button
            type="button"
            disabled={!canAdd}
            onClick={() =>
              onSelect({
                kind: "new",
                data: {
                  name: creating.name.trim(),
                  homeKennelId: creating.homeKennelId,
                  phone: creating.phone?.trim() || undefined,
                },
              })
            }
            className="flex-1 h-12 rounded-lg bg-slate-900 text-white font-medium active:bg-slate-700 disabled:bg-slate-300"
          >
            Add hare
          </button>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={onClose} title="Add hare">
      <input
        ref={inputRef}
        type="search"
        inputMode="search"
        placeholder="Search hashers…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => loadHashers().catch(() => {})}
        className="w-full h-12 px-4 rounded-lg bg-slate-100 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
      />
      <ul className="mt-3 divide-y divide-slate-200">
        {results.map((h) => (
          <li key={h.id}>
            <button
              type="button"
              onClick={() => onSelect({ kind: "existing", hasher: h })}
              className="w-full text-left py-3 active:bg-slate-50"
            >
              <div className="font-medium text-slate-900">{h.name}</div>
              <div className="text-sm text-slate-500">{h.homeKennel}</div>
            </button>
          </li>
        ))}
        {query.trim() && (
          <li>
            <button
              type="button"
              onClick={() =>
                setCreating({
                  name: query.trim(),
                  homeKennelId: defaultHomeKennelId,
                  phone: "",
                })
              }
              className="w-full text-left py-3 font-medium text-slate-900 active:bg-slate-50"
            >
              + Create new hasher named "{query.trim()}"
            </button>
          </li>
        )}
      </ul>
    </Overlay>
  );
}

function Overlay({
  children,
  onClose,
  title,
}: {
  children: React.ReactNode;
  onClose: () => void;
  title: string;
}) {
  return (
    <div
      className="fixed inset-0 z-20 bg-slate-900/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-4 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full text-slate-500 active:bg-slate-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full h-12 px-3 rounded-lg border border-slate-300 text-base focus:outline-none focus:ring-2 focus:ring-slate-900";

/** Parse a dollars string to integer cents, or null if blank/invalid. */
function toCents(dollars: string): number | null {
  const trimmed = dollars.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function todayLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
